import Site from 'es/app/business-logic/domain-models/Site';
import { AccessControlCollection, ChallengeType } from 'es/app/security/IAccessControlSet';
import { useUserStore } from 'es/app/store/GlobalStore';

/**
 * Determines if the user is authorized for the nominated access control/s. This is a thin wrapper around the equivalent
 * method in userStore.
 *
 * <strong>Note:</strong> components that use this hook MUST be wrapped in an `observer()` HOC, otherwise they will not
 * be notified of changes to the access controls collection.
 *
 * @param requiredAccessControl The access control/s to authorize
 * @param site The site to authorize, or `null` for the currently active site
 * @param challengeType Determines if access should be granted on the condition that the user has permission
 * to any (default) or all access controls.
 * @returns `null` if access controls are still loading for the given auth domain; `true` if access is permitted,
 * or `false` if access is not permitted.
 */
const useAccessControlAuthorization = (
    requiredAccessControl: string | AccessControlCollection | undefined,
    site: Site | null = null,
    challengeType = ChallengeType.Any
) => {
    const store = useUserStore();
    if (!requiredAccessControl) return true;
    if (!site) return true;
    return store.isAuthorized(requiredAccessControl, site || store.activeSite, challengeType);
};

export default useAccessControlAuthorization;
